import { AlertTriangle } from 'lucide-react';
import type { DuplicateDetectionResult } from '../../../lib/services/duplicateDetectionService';

interface DuplicateNoticeProps {
  result: DuplicateDetectionResult | null;
  /** Motivi da mostrare qui, accanto ai campi della sezione (es. Email/Telefono) */
  reasons: string[];
  /** "Apri scheda esistente": apre la scheda trovata e chiude il drawer */
  onOpenClient?: (clientId: string) => void;
  /** In creazione la bozza resta salvata; in modifica non c'e' bozza */
  draftKept?: boolean;
}

const OTHER_LABEL = { contatto: 'contatto', segnalatore: 'segnalatore', utente: 'utente' } as const;

/**
 * Avviso "possibile doppione" sotto i campi che lo hanno generato. Solo un avviso:
 * il salvataggio resta possibile.
 */
export function DuplicateNotice({ result, reasons, onOpenClient, draftKept }: DuplicateNoticeProps) {
  if (!result?.has_duplicates) return null;
  const clients = result.duplicates.filter((d) => d.match_reason.some((r) => reasons.includes(r)));
  const others = reasons.includes('Email') ? result.others : [];
  if (clients.length === 0 && others.length === 0) return null;

  const items = [
    ...clients.map((d: any) => {
      const isPerson = d.entity_type === 'individual' && !d.stato_cliente;
      const name = d.business_name || d.contact_name || d.email || 'Scheda';
      return `${isPerson ? 'referente ' : ''}${name} (${d.match_reason.filter((r: string) => reasons.includes(r)).join(', ')})`;
    }),
    ...others.map((o) => `${OTHER_LABEL[o.kind]} ${o.name}`),
  ];
  const shown = items.slice(0, 3);
  const firstClient = clients[0];

  return (
    <div className="flex items-start gap-2 px-1.5 py-1.5">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#D9730D]" strokeWidth={1.5} />
      <p className="text-[12px] leading-snug text-[#37352F] dark:text-gray-200">
        Già presente: {shown.join('; ')}{items.length > shown.length ? ` e altri ${items.length - shown.length}` : ''}.
        {onOpenClient && firstClient?.id && (
          <>
            {' '}
            <button
              type="button"
              onClick={() => onOpenClient(firstClient.id)}
              className="font-medium text-[#2383E2] hover:underline"
            >
              Apri scheda esistente
            </button>
            {draftKept && <span className="text-[#787774] dark:text-gray-400"> (la bozza resta salvata)</span>}
          </>
        )}
      </p>
    </div>
  );
}
